import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Scale, CreditCard, Shield, Ban } from 'lucide-react';

export const TermsOfServicePage = () => {
  const navigate = useNavigate();
  const lastUpdated = "December 2025";

  return (
    <div className="min-h-screen bg-medex-bg">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center space-x-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-neon-purple/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-neon-purple" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-white">Terms of Service</h1>
              <p className="text-slate-400">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="glass-card p-8 space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Agreement to Terms</h2>
              <div className="text-slate-300 space-y-4">
                <p>
                  By accessing or using MedexOVA's services, including our website, dispatch platform, payment processing, 
                  insurance services, and related products ("Services"), you agree to be bound by these Terms of Service 
                  ("Terms"). If you do not agree to these Terms, do not use our Services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-neon-purple" />
                Service Description
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>MedexOVA provides technology solutions for Non-Emergency Medical Transportation (NEMT) including:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li><strong className="text-white">OVA Dispatch:</strong> AI-powered trip booking and dispatch management</li>
                  <li><strong className="text-white">Medex Pay:</strong> Payment processing and claims management</li>
                  <li><strong className="text-white">Medex Group Insurance:</strong> activity-based insurance coverage</li>
                  <li><strong className="text-white">NEMT Vision:</strong> AI dash cam and safety monitoring</li>
                  <li><strong className="text-white">Credentialing:</strong> Document and compliance management</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Scale className="w-5 h-5 mr-2 text-neon-purple" />
                User Responsibilities
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>As a user of our Services, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li>Provide accurate and complete information during registration</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Maintain proper licensing and insurance for your transportation operations</li>
                  <li>Not use the Services for any unlawful purpose</li>
                  <li>Not attempt to interfere with the proper functioning of the Services</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-neon-purple" />
                Payment Terms
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>For paid Services:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li>Fees are charged according to the pricing agreed upon at signup</li>
                  <li>activity-based insurance is billed based on actual trip mileage</li>
                  <li>Payment is due according to the billing cycle specified in your agreement</li>
                  <li>Late payments may result in service suspension</li>
                  <li>All fees are non-refundable unless otherwise specified</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Insurance Terms</h2>
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="text-slate-300">
                    <p className="font-semibold text-white mb-2">Important Insurance Notice</p>
                    <p className="text-sm">
                      Medex Group Insurance is subject to separate policy terms and conditions provided by our insurance 
                      carrier partners. Coverage is contingent on meeting eligibility requirements, including minimum 
                      participation thresholds per state (20 providers). Insurance availability varies by state and is 
                      subject to regulatory approval.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Data and Software Requirements</h2>
              <div className="text-slate-300 space-y-4">
                <p>For certain Services, particularly activity-based insurance:</p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li>You must use MedexOVA dispatch software OR provide API access to your existing dispatch system</li>
                  <li>Trip data must be accurate and complete for proper billing</li>
                  <li>Tampering with or falsifying trip data is grounds for immediate termination</li>
                  <li>We reserve the right to audit trip data for accuracy</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Ban className="w-5 h-5 mr-2 text-neon-purple" />
                Limitation of Liability
              </h2>
              <div className="text-slate-300 space-y-4">
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, MEDEXOVA SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                  SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, 
                  OR BUSINESS OPPORTUNITIES.
                </p>
                <p>
                  Our total liability for any claim arising from these Terms or the Services shall not exceed the 
                  amount paid by you to MedexOVA in the twelve (12) months preceding the claim.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Termination</h2>
              <div className="text-slate-300 space-y-4">
                <p>
                  Either party may terminate service with 30 days written notice. MedexOVA may terminate immediately 
                  for breach of these Terms, non-payment, or illegal activity. Upon termination:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-slate-400">
                  <li>All outstanding fees become immediately due</li>
                  <li>Your access to the Services will be revoked</li>
                  <li>We will retain data as required by law or for legitimate business purposes</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Governing Law</h2>
              <div className="text-slate-300">
                <p>
                  These Terms shall be governed by the laws of the State of Delaware, without regard to its conflict 
                  of law provisions. Any disputes arising from these Terms shall be resolved through binding arbitration 
                  in accordance with the rules of the American Arbitration Association.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Changes to Terms</h2>
              <div className="text-slate-300">
                <p>
                  We may modify these Terms at any time. Material changes will be communicated via email or through 
                  the Services. Continued use after changes constitutes acceptance of the new Terms.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Contact</h2>
              <div className="text-slate-300">
                <p>For questions about these Terms:</p>
                <p className="mt-2 text-slate-400">
                  Email: <a href="mailto:legal@medexova.com" className="text-neon-purple hover:underline">legal@medexova.com</a>
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
